/**
 * LieferscheinPDF
 *
 * Taşıma irsaliyesi: ürünlerin müşteriye ulaştırılacağı belge. Fiyat gizli.
 * İçerik: resim + ürün + miktar. Lagerort BİLİNÇLİ olarak yok — depo
 * konumu yalnızca Kommissionierschein'da gösterilir.
 * İki nüsha desteklenir ('Lager' ve 'Kunde'); nüsha adı belgenin
 * başlığında ve dosya adında görünür.
 */
import {
  fetchPdfSettings,
  downloadPdfFromHtml,
  wrapPdfDocument,
  renderCompanyHeader,
  renderFooter,
  formatDate,
  escapeHtml,
} from '@/lib/pdfRenderer';
import { getOrderItems } from '@/lib/orderFields';
import { getVorlage, textZuHtml } from '@/lib/dokumentVorlagen';

/** Kalem için ürün görseli: varyant görseli > ürün görseli > item.image */
function resolveItemImage(item, products = []) {
  const product = products.find((p) => p.id === item.product_id);
  const variantValue = item.variant_label || item.variant_value || item.variant;
  if (product && variantValue) {
    let variants = product.variants || [];
    if (typeof variants === 'string') {
      try { variants = JSON.parse(variants); } catch { variants = []; }
    }
    const variant = (Array.isArray(variants) ? variants : [])
      .find((v) => v.value === variantValue);
    if (variant?.image) return variant.image;
  }
  return item.image || product?.image_url || '';
}

function renderTemplate(order, settings, { exemplar = 'Kunde', products = [] } = {}) {
  const today = formatDate(new Date());
  const noteAt = order.delivery_note_at ? formatDate(order.delivery_note_at) : today;
  const items = getOrderItems(order);
  const isLagerCopy = exemplar === 'Lager';
  const vorlage = getVorlage(settings, 'lieferschein');
  const ctx = {
    'kunde.name': order.lead_name || '', 'kunde.nummer': (order.lead_id || '').substring(0, 4).toUpperCase(),
    'firma.name': settings.company_name, 'firma.geschaeftsfuehrer': settings.geschaeftsfuehrer, 'firma.ansprechpartner': settings.ansprechpartner,
    'beleg.nummer': order.delivery_note_number || '', 'beleg.datum': noteAt, 'bestellung.nummer': order.order_number || '',
  };
  const einleitungHtml = textZuHtml(vorlage.einleitung, ctx);

  const rows = items.map((item) => {
    const imageUrl = resolveItemImage(item, products);
    const imageCell = imageUrl
      ? `<img src="${escapeHtml(imageUrl)}" alt=""
             style="width:40px;height:40px;object-fit:contain;border:1px solid #e5e7eb;border-radius:6px;background:#fff"
             onerror="this.style.display='none'" />`
      : `<div style="width:40px;height:40px;border:1px dashed #d1d5db;border-radius:6px;margin:0 auto"></div>`;

    return `
    <tr>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;width:56px">${imageCell}</td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb">
        <div style="font-weight:700;font-size:12px">${escapeHtml(item.product_name || item.name || '')}</div>
        ${item.variant_label || item.variant_value || item.variant
          ? `<div style="font-size:10px;color:#555">Variante: ${escapeHtml(item.variant_label || item.variant_value || item.variant)}</div>` : ''}
        ${item.product_sku
          ? `<div style="font-size:10px;color:#999;font-family:monospace">SKU: ${escapeHtml(item.product_sku)}</div>` : ''}
      </td>
      <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">
        <div style="font-weight:700;font-size:15px">${item.quantity}</div>
        <div style="font-size:9px;color:#999;text-transform:uppercase">${escapeHtml(item.unit || 'Stück')}</div>
      </td>
    </tr>
  `;
  }).join('');

  // Yalnızca gövde HTML'i döner — wrapPdfDocument ile sarılır.
  return `
      ${renderCompanyHeader(settings)}

      <div style="display:flex;justify-content:space-between;margin-bottom:24px">
        <div>
          <h1 style="font-size:22px;margin:0 0 6px 0;color:#4338ca">LIEFERSCHEIN</h1>
          <div style="color:#666;font-size:11px">${textZuHtml(vorlage.untertitel, ctx)}</div>
          <div style="display:inline-block;margin-top:6px;padding:3px 10px;border-radius:99px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;${isLagerCopy
            ? 'background:#fef3c7;color:#92400e;border:1px solid #fcd34d'
            : 'background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe'}">
            Exemplar: ${isLagerCopy ? 'Lager / Kommissionierung' : 'Kunde'}
          </div>
        </div>
        <div style="text-align:right;font-size:11px;color:#333">
          <div><strong>Nummer:</strong> ${escapeHtml(order.delivery_note_number || '(wird vergeben)')}</div>
          <div><strong>Datum:</strong> ${noteAt}</div>
          <div><strong>Bestellung:</strong> ${escapeHtml(order.order_number || '')}</div>
          ${order.picking_number ? `<div><strong>Kommissionier-Nr:</strong> ${escapeHtml(order.picking_number)}</div>` : ''}
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px;font-size:11px">
        <div style="padding:14px;background:#eef2ff;border-left:3px solid #6366f1">
          <div style="font-size:9px;color:#4338ca;font-weight:700;text-transform:uppercase;margin-bottom:6px">Empfänger</div>
          <div style="font-weight:700;font-size:12px">${escapeHtml(order.lead_name || '')}</div>
          ${order.lead_address ? `<div style="color:#555;margin-top:2px">${escapeHtml(order.lead_address)}</div>` : ''}
          ${order.lead_phone ? `<div style="color:#555;margin-top:2px">Tel: ${escapeHtml(order.lead_phone)}</div>` : ''}
        </div>
        <div style="padding:14px;background:#f8fafc;border-left:3px solid #64748b">
          <div style="font-size:9px;color:#334155;font-weight:700;text-transform:uppercase;margin-bottom:6px">Lieferung</div>
          ${order.lieferdatum ? `<div><strong>Lieferdatum:</strong> ${formatDate(order.lieferdatum)}</div>` : ''}
          ${order.zahlungsart ? `<div><strong>Zahlung:</strong> ${escapeHtml(order.zahlungsart)}</div>` : ''}
          ${order.notes || order.notizen
            ? `<div style="margin-top:6px;color:#555">${escapeHtml(order.notes || order.notizen)}</div>`
            : ''}
        </div>
      </div>

      ${einleitungHtml ? `<p style="font-size:11px;line-height:1.5;margin:0 0 14px">${einleitungHtml}</p>` : ''}

      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:24px">
        <thead>
          <tr style="background:#172554;color:#fff">
            <th style="padding:10px;text-align:center;font-size:10px;text-transform:uppercase;width:56px">Bild</th>
            <th style="padding:10px;text-align:left;font-size:10px;text-transform:uppercase">Artikel</th>
            <th style="padding:10px;text-align:center;font-size:10px;text-transform:uppercase;width:80px">Menge</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div style="margin-top:48px;display:grid;grid-template-columns:1fr 1fr;gap:32px">
        <div>
          <div style="border-bottom:1px solid #111;height:56px"></div>
          <div style="font-size:10px;color:#555;margin-top:4px">${textZuHtml(vorlage.unterschrift_links, ctx)}</div>
        </div>
        <div>
          <div style="border-bottom:1px solid #111;height:56px"></div>
          <div style="font-size:10px;color:#555;margin-top:4px">${textZuHtml(vorlage.unterschrift_rechts, ctx)}</div>
        </div>
      </div>

      ${vorlage.hinweis ? `<div style="margin-top:24px;padding:12px;background:#fef3c7;border-left:3px solid #f59e0b;font-size:10px;color:#78350f">
        <strong>Hinweis:</strong> ${textZuHtml(vorlage.hinweis, ctx)}
      </div>` : ''}

      ${renderFooter(settings)}
  `;
}

/** Gövde HTML üretici (kombine PDF için dışa açık). */
export function buildLieferscheinBody(order, settings, options = {}) {
  return renderTemplate(order, settings, options);
}

/**
 * @param {object} order
 * @param {object} [customSettings] — test/özel PDF ayarları
 * @param {{ exemplar?: 'Kunde'|'Lager', products?: any[] }} [options]
 */
export async function downloadLieferschein(order, customSettings, options = {}) {
  const settings = customSettings || await fetchPdfSettings();
  const exemplar = options.exemplar || 'Kunde';
  const body = renderTemplate(order, settings, { ...options, exemplar });
  const html = wrapPdfDocument(body, `Lieferschein ${order.delivery_note_number || ''}`);
  const base = order.delivery_note_number || `LS-${Date.now()}`;
  const fileName = exemplar === 'Lager' ? `${base}_Lager` : `${base}_Kunde`;
  await downloadPdfFromHtml(html, fileName);
}

/** İki nüshayı (Lager + Kunde) arka arkaya indirir. */
export async function downloadLieferscheinBeideExemplare(order, products = [], customSettings) {
  const settings = customSettings || await fetchPdfSettings();
  await downloadLieferschein(order, settings, { exemplar: 'Lager', products });
  await downloadLieferschein(order, settings, { exemplar: 'Kunde', products });
}

export default { downloadLieferschein, downloadLieferscheinBeideExemplare, buildLieferscheinBody };
